import { InMemoryNdtAssetAllocationRepository } from "../infrastructure/database/memory/in-memory-ndt-asset-allocation-repository.js";
import { createNdtAssetUsageLister } from "../application/services/ndt-asset-traceability.js";
import { createNdtReportAssetValidator } from "../application/services/ndt-asset-report-usage.js";
import "../infrastructure/documents/builders/index.js";
import { EquipmentService, type EquipmentHistoryChecker } from "../equipment/equipment-service.js";
import { SignatureService } from "../application/services/signature-service.js";
import { EngineeringService } from "../application/services/engineering-service.js";
import { InstrumentService } from "../equipment/instrument-service.js";
import { SafetyDeviceService } from "../equipment/safety-device-service.js";
import type { TechnicalAssetHierarchyLookup } from "../equipment/technical-asset-hierarchy.js";
import { InspectionService } from "../application/services/inspection-service.js";
import { InMemoryEquipmentRepository } from "../infrastructure/database/memory/in-memory-equipment-repository.js";
import { InMemoryInstrumentRepository, InMemorySafetyDeviceRepository } from "../infrastructure/database/memory/in-memory-technical-asset-repositories.js";
import { InMemoryInspectionRepository, InMemoryChecklistRepository } from "../infrastructure/database/memory/in-memory-inspection-repositories.js";
import { InMemoryIntegrityReportRepository, InMemoryRecommendationRepository } from "../infrastructure/database/memory/in-memory-report-repositories.js";
import { ReportService } from "../application/services/report-service.js";
import { InspectionBookService } from "../application/services/inspection-book-service.js";
import { InMemoryInspectionBookRepository } from "../infrastructure/database/memory/in-memory-book-repositories.js";
import { InMemoryAdminRepository } from "../infrastructure/database/memory/in-memory-admin-repository.js";
import { InMemoryIdentityRepository } from "../infrastructure/database/memory/in-memory-identity-repositories.js";
import { InMemoryCompanyRepository } from "../infrastructure/database/memory/in-memory-company-repository.js";
import { InMemoryRdoRepository } from "../infrastructure/database/memory/in-memory-rdo-repository.js";
import { AdminService } from "../administration/admin-service.js";
import { RdoService } from "../application/services/rdo-service.js";
import { ServiceFrontService } from "../application/services/service-front-service.js";
import { InMemoryNdtAssetRepository } from "../infrastructure/database/memory/in-memory-ndt-asset-repositories.js";
import { NdtAssetService } from "../application/services/ndt-asset-service.js";
import { NdtEquipmentImportService } from "../application/services/ndt-equipment-import-service.js";
import { InMemoryProjectRepository } from "../infrastructure/database/memory/in-memory-project-repository.js";
import { ProjectService } from "../application/services/project-service.js";
import { RbacAuthorizationService } from "../permissions/authorization.js";
import { PermissionCode } from "../permissions/permissions.js";
import { AppError, ErrorCode } from "../shared/errors/app-error.js";
import { MinutaService } from "../application/services/minuta-service.js";
import { InMemoryMinutaRepository } from "../infrastructure/database/memory/in-memory-minuta-repository.js";
import { MeReportService } from "../application/services/me-report-service.js";
import { InMemoryMeReportRepository } from "../infrastructure/database/memory/in-memory-me-report-repository.js";
import { LpReportService } from "../application/services/lp-report-service.js";
import { LpConsumableService } from "../application/services/lp-consumable-service.js";
import { InMemoryLpReportRepository } from "../infrastructure/database/memory/in-memory-lp-report-repository.js";
import { InMemoryLpConsumableRepository } from "../infrastructure/database/memory/in-memory-lp-consumable-repository.js";
import { PmConsumableService } from "../application/services/pm-consumable-service.js";
import { PmReportService } from "../application/services/pm-report-service.js";
import { ConventionalUsReportService } from "../application/services/conventional-us-report-service.js";
import { BscanReportService } from "../application/services/bscan-report-service.js";
import { NdtReportCatalogService } from "../application/services/ndt-report-catalog-service.js";
import { InMemoryPmReportRepository, InMemoryConventionalUsReportRepository, InMemoryBscanReportRepository } from "../infrastructure/database/memory/in-memory-ndt-report-repository.js";
import type { BscanReportRepository, ConventionalUsReportRepository, PmReportRepository } from "../repositories/operational-repositories.js";
import { InMemoryPmConsumableRepository } from "../infrastructure/database/memory/in-memory-pm-consumable-repository.js";
import { NdtProcedureService } from "../application/services/ndt-procedure-service.js";
import { InMemoryNdtProcedureRepository } from "../infrastructure/database/memory/in-memory-ndt-procedure-repository.js";
import { NdtCatalogService } from "../application/services/ndt-catalog-service.js";
import { EngineeringCalculationService } from "../application/services/engineering-calculation-service.js";
import { InMemoryCalculationRepository } from "../infrastructure/database/memory/in-memory-calculation-repository.js";
import { IntegrityCatalogService } from "../application/services/integrity-catalog-service.js";
import { InMemoryIntegrityCatalogRepository } from "../infrastructure/database/memory/in-memory-integrity-catalog-repository.js";
import { AvrReportService } from "../application/services/avr-report-service.js";
import { MeAvrSourceAdapter, BscanAvrSourceAdapter } from "../application/services/avr-sources.js";
import { InMemoryAvrReportRepository } from "../infrastructure/database/memory/in-memory-avr-report-repository.js";
import { TechnicalReviewService } from "../application/services/technical-review-service.js";
import { InMemoryTechnicalReviewRepository } from "../infrastructure/database/memory/in-memory-technical-review-repository.js";
import { avrReviewSource, integrityReviewSource, lpReviewAdapter, meReviewAdapter, pmReviewAdapter, uscReviewAdapter, bscanReviewAdapter } from "../application/services/technical-review-adapters.js";
import { TechnicalCorrectionService } from "../application/services/technical-correction-service.js";
import { InMemoryTechnicalCorrectionRepository } from "../infrastructure/database/memory/in-memory-technical-correction-repository.js";
import { DocumentHoldService } from "../application/services/document-hold-service.js";
import { InMemoryDocumentHoldRepository } from "../infrastructure/database/memory/in-memory-document-hold-repository.js";
import { EngineeringDashboardService } from "../application/services/engineering-dashboard-service.js";
import type { EngineeringNdtRow, IntegrityReportDescription } from "../application/services/engineering-service.js";
import { ProjectResponsibilityService } from "../application/services/project-responsibility-service.js";
import { InspectionBookConsolidatorService } from "../application/services/inspection-book-consolidator-service.js";
import { PipingSystemLineListService } from "../application/services/piping-system-line-list-service.js";
import { TechnicalDocumentService } from "../application/services/technical-document-service.js";
import { EquipmentImportService } from "../application/services/equipment-import-service.js";
import { FieldControlService } from "../application/services/field-control-service.js";
import { UserHomeService } from "../application/services/user-home-service.js";
import { ImageAnnotationService } from "../application/services/image-annotation-service.js";
import { InMemoryFieldControlRepository } from "../infrastructure/database/memory/in-memory-field-control-repository.js";
import { InMemoryImportBatchRepository } from "../infrastructure/database/memory/in-memory-import-batch-repository.js";
import { InMemoryTechnicalDocumentRepository } from "../infrastructure/database/memory/in-memory-technical-document-repository.js";
import { DurableDocumentStorageService, InMemoryDocumentStorageService, localStorageLegacyBlobs } from "../application/services/document-storage-service.js";
import { IndexedDbBlobPersistence } from "../infrastructure/storage/indexeddb-blob-persistence.js";
import { ENGINEERING_WORKFLOW_STATUS_LABELS, workflowStatusOfReview } from "../domain/review/engineering-workflow-status.js";
import { TECHNICAL_DOCUMENT_TYPE_LABELS } from "../domain/review/technical-document-review.js";
import { InMemoryNdtCatalogRepository } from "../infrastructure/database/memory/in-memory-ndt-catalog-repository.js";
import { systemResetService } from "./system-reset-service.js";
import type { RequestContext } from "../application/context/request-context.js";
import type { AuditRecorder } from "../audit/audit-recorder.js";

const mockAudit: AuditRecorder = {
  record: async () => { return { id: 'audit', clientId: null, correlationId: '' as any, userId: '', professionalId: null, action: 'AUDIT' as any, entityType: '', entityId: '', previousState: null, newState: null, metadata: {}, timestamp: new Date().toISOString() } as any; }
};
import { InMemoryBackupRepository } from "../infrastructure/database/memory/in-memory-backup-repository.js";
import { BackupService } from "../application/services/backup-service.js";
import { RestoreService } from "../application/services/restore-service.js";
import { createDefaultBackupCollectors } from "../infrastructure/backup/default-entity-collectors.js";
import { runAutoSeeder } from "./auto-seeder.js";
import { wireSystemSettings } from "./system-settings-wiring.js";
import { wireStudioDesign } from "./studio-design-wiring.js";
import { wireDashboardShare } from "./dashboard-share-wiring.js";
import { InMemoryDashboardShareRepository } from "../infrastructure/database/memory/in-memory-dashboard-share-repository.js";
import { wireInspectIaReview } from "./inspect-ia-review-wiring.js";
import { InMemoryAIReviewRepository } from "../infrastructure/database/memory/in-memory-ai-review-repository.js";
import type { InspectIaReviewService } from "../application/services/inspect-ia-review-service.js";
import type { DashboardShareService, DashboardSharePublicService } from "../application/services/dashboard-share-service.js";
import { InMemoryStudioDesignRepository } from "../infrastructure/database/memory/in-memory-studio-design-repository.js";
import type { StudioDesignService } from "../application/services/studio-design-service.js";
import type { StudioDesignProvider } from "../application/services/studio-design-provider.js";
import { browserSettingsProbe } from "./browser-settings-probe.js";
import { InMemorySystemSettingsRepository } from "../infrastructure/database/memory/in-memory-system-settings-repository.js";
import { UnavailableSecretStore } from "../repositories/system-settings-repository.js";
import type { SystemSettingsService } from "../application/services/system-settings-service.js";
import type { SystemSettingsProvider } from "../application/services/system-settings-provider.js";
import type { DocumentNumberingService } from "../application/services/document-numbering-service.js";
import type { EquipmentRepository } from "../equipment/equipment-domain.js";
import type { InspectionRepository, ChecklistRepository, IntegrityReportRepository, RecommendationRepository, MinutaRepository, MeReportRepository, LpReportRepository } from "../repositories/operational-repositories.js";
import type { InstrumentRepository, SafetyDeviceRepository } from "../equipment/equipment-domain.js";

/**
 * Builds the equipment-deletion history checker used by EquipmentService:
 * decides whether a deletion is denied (a linked Integrity Report exists)
 * and, when allowed, cascades removal of the equipment's dependencies.
 * Extracted as a standalone factory (rather than an inline object in the
 * BrowserPlatform constructor) so it can be exercised directly against real
 * in-memory repositories in integration tests, without instantiating the
 * full BrowserPlatform (which has module-level side effects — an exported
 * singleton and a fire-and-forget auto-seeder).
 */
export function createEquipmentHistoryChecker(repos: {
  equipmentRepo: EquipmentRepository;
  inspectionRepo: InspectionRepository;
  checklistRepo: ChecklistRepository;
  reportRepo: IntegrityReportRepository;
  recommendationRepo: RecommendationRepository;
  instrumentRepo: InstrumentRepository;
  safetyDeviceRepo: SafetyDeviceRepository;
}): EquipmentHistoryChecker {
  const { equipmentRepo, inspectionRepo, checklistRepo, reportRepo, recommendationRepo, instrumentRepo, safetyDeviceRepo } = repos;

  return {
    hasTechnicalHistory: async (clientId: string, equipmentId: string) => {
      const dummyContext = { activeClientId: clientId, permissions: new Set(), correlationId: '', userId: '' } as any;
      const inspections = await inspectionRepo.findByEquipment(dummyContext, equipmentId as any);
      const snapshots = await equipmentRepo.listSnapshotsByEquipment(clientId as any, equipmentId as any);
      const documents = await equipmentRepo.listDocuments(clientId as any, equipmentId as any);
      const recommendations = await recommendationRepo.findByEquipment(dummyContext, equipmentId as any);
      const linkedInstruments = await instrumentRepo.listInstruments(clientId as any, { equipmentId });
      const linkedDevices = await safetyDeviceRepo.listSafetyDevices(clientId as any, { protectedEquipmentId: equipmentId });

      // A Relatório de Integridade (IntegrityReport) links to Equipment only
      // indirectly, through Inspection (integrity_reports.inspection_id) — it
      // has no equipmentId field. Resolve it via the real relationship instead
      // of a non-existent `report.equipmentId` (that comparison always
      // returned an empty array, making reportsCount silently 0 even when a
      // report existed — see docs/CLAUDE_HANDOFF.md, Bloco 4).
      const reports = (
        await Promise.all(inspections.map(ins => reportRepo.findByInspection(dummyContext, ins.id)))
      ).filter((r): r is NonNullable<typeof r> => r !== null);

      const hasHistory = inspections.length > 0 || snapshots.length > 0 || documents.length > 0 || recommendations.length > 0 || linkedInstruments.length > 0 || linkedDevices.length > 0 || reports.length > 0;
      return {
        hasHistory,
        details: {
          inspectionsCount: inspections.length,
          snapshotsCount: snapshots.length,
          reportsCount: reports.length,
          recommendationsCount: recommendations.length,
          avrCount: 0,
          documentsCount: documents.length,
          linkedInstrumentsCount: linkedInstruments.length,
          linkedDevicesCount: linkedDevices.length
        }
      };
    },
    deleteDependencies: async (clientId: string, equipmentId: string) => {
      const dummyContext = { activeClientId: clientId, permissions: new Set(), correlationId: '', userId: '' } as any;

      // Deletion order is children -> parents so no step ever references an
      // already-removed parent. Errors are not swallowed here: a failure at
      // any step must abort the whole operation rather than silently
      // proceeding to remove the Equipment record with orphaned
      // dependencies left behind (no real DB transaction exists in this
      // in-memory/localStorage runtime, so this is the closest available
      // approximation of atomicity — see docs/CLAUDE_HANDOFF.md for the
      // documented follow-up once a real backend/transaction exists).

      // 1. Checklists (+ components/photos) — children of each Inspection.
      const inspections = await inspectionRepo.findByEquipment(dummyContext, equipmentId as any);
      if (!checklistRepo.deleteByInspection) {
        throw new Error('ChecklistRepository.deleteByInspection is required to cascade equipment deletion safely');
      }
      for (const ins of inspections) {
        await checklistRepo.deleteByInspection(dummyContext, ins.id);
      }

      // 2. Inspections themselves.
      for (const ins of inspections) {
        await inspectionRepo.delete(dummyContext, ins.id);
      }

      // 3. Recommendations.
      if (!recommendationRepo.delete) {
        throw new Error('RecommendationRepository.delete is required to cascade equipment deletion safely');
      }
      const recs = await recommendationRepo.findByEquipment(dummyContext, equipmentId as any);
      for (const r of recs) {
        await recommendationRepo.delete(dummyContext, r.id);
      }

      // 4. Equipment snapshots.
      await equipmentRepo.deleteSnapshotsByEquipment(clientId as any, equipmentId as any);

      // 5. Equipment documents (technical attachments — no retention/status
      // field exists on EquipmentDocument today, so there is no finalized
      // document concept to preserve here).
      const documents = await equipmentRepo.listDocuments(clientId as any, equipmentId as any);
      for (const doc of documents) {
        await equipmentRepo.removeDocument(clientId as any, doc.id);
      }

      // 6. Unlink (never delete) safety devices and instruments — they are
      // assets with their own lifecycle independent from this Equipment.
      const instruments = await instrumentRepo.listInstruments(clientId as any, { equipmentId });
      for (const inst of instruments) {
         await instrumentRepo.saveInstrument({ ...inst, equipmentId: null as any });
      }

      const devices = await safetyDeviceRepo.listSafetyDevices(clientId as any, { protectedEquipmentId: equipmentId });
      for (const dev of devices) {
         await safetyDeviceRepo.saveSafetyDevice({ ...dev, protectedEquipmentId: null as any });
      }

      // NOTE: AVR (avr-domain.ts) and operational NDT reports
      // (ndt-operational-domain.ts) both declare an `equipmentId` field but
      // have no in-memory repository wired into this composition root —
      // there is nothing to query or cascade for them today. Documented as
      // a pendency in docs/CLAUDE_HANDOFF.md, not implemented in this block.
    }
  };
}

/**
 * Builds the User-deletion history checker used by AdminService (Bloco 8):
 * decides whether a User must be preserved (soft delete / DESATIVAR only)
 * because it authored an Engineering Minuta analysis/validation/approval
 * (Minuta.analysisStartedBy / validatedByUserId / approvedBy — all store a
 * userId, see src/domain/report/minuta-domain.ts). A User with no such
 * authorship (and no linked Professional, checked separately in
 * AdminService) is safe to hard-delete. Extracted as a standalone factory,
 * same reasoning as createEquipmentHistoryChecker above.
 */
export function createUserHistoryChecker(minutaRepo: MinutaRepository) {
  return async (userId: string): Promise<boolean> => {
    if (!minutaRepo.listAll) return false;
    const dummyContext = { activeClientId: '', permissions: new Set(), correlationId: '', userId: '' } as any;
    const allMinutas = await minutaRepo.listAll(dummyContext);
    return allMinutas.some(
      m => m.analysisStartedBy === userId || m.validatedByUserId === userId || m.approvedBy === userId
    );
  };
}

/**
 * Builds the usage checker consumed by NdtProcedureService (Bloco 14): a
 * procedure referenced by any report must never be physically deleted, only
 * inactivated — same rule already applied to Equipment and Instruments.
 * Today only the ME report links a procedure; as LP/PM/B-Scan reports gain
 * their own storage, add them here so the guarantee keeps holding.
 */
export function createNdtProcedureUsageChecker(
  meReportRepo: MeReportRepository,
  lpReportRepo?: LpReportRepository,
  others: Array<{ listAll?: () => Promise<readonly { procedureId: string | null }[]> }> = []
) {
  return async (procedureId: string): Promise<boolean> => {
    if (meReportRepo.listAll) {
      const reports = await meReportRepo.listAll();
      if (reports.some(report => report.procedureId === procedureId)) return true;
    }
    // O LP também aponta procedimentos: um procedimento usado num LP é
    // inativado, nunca apagado.
    if (lpReportRepo?.listAll) {
      const reports = await lpReportRepo.listAll();
      if (reports.some(report => report.procedureId === procedureId)) return true;
    }
    // PM e US-Convencional: a mesma garantia.
    for (const repo of others) {
      if (!repo.listAll) continue;
      const reports = await repo.listAll();
      if (reports.some(report => report.procedureId === procedureId)) return true;
    }
    return false;
  };
}

/**
 * Same guarantee for the technical catalogs (Bloco 15): a norma, critério or
 * acoplante already used by a report is inactivated, never deleted.
 */
/** Same guarantee for END assets: an Aparelho, Transdutor or Bloco already used by a report is inactivated, never deleted. */
export function createNdtAssetUsageChecker(meReportRepo: MeReportRepository, pmReportRepo?: PmReportRepository, uscReportRepo?: ConventionalUsReportRepository, bscanReportRepo?: BscanReportRepository) {
  return async (assetId: string): Promise<boolean> => {
    if (meReportRepo.listAll) {
      const reports = await meReportRepo.listAll();
      if (reports.some(report =>
        report.deviceAssetId === assetId ||
        report.transducerAssetId === assetId ||
        report.calibrationBlockAssetId === assetId
      )) return true;
    }
    // Yoke usado num Relatório PM; aparelho, transdutores e bloco usados num US-Convencional.
    if (pmReportRepo?.listAll) {
      const reports = await pmReportRepo.listAll();
      if (reports.some(report => report.yokeAssetId === assetId)) return true;
    }
    if (uscReportRepo?.listAll) {
      const reports = await uscReportRepo.listAll();
      if (reports.some(report =>
        report.deviceAssetId === assetId ||
        report.calibrationBlockAssetId === assetId ||
        (report.transducers || []).some(t => t.assetId === assetId)
      )) return true;
    }
    // Aparelho, transdutor e bloco de um B-Scan: excluir um ativo em uso
    // deixaria o relatório apontando para um cadastro que não existe mais.
    if (bscanReportRepo?.listAll) {
      const reports = await bscanReportRepo.listAll();
      if (reports.some(report =>
        report.deviceAssetId === assetId ||
        report.transducerAssetId === assetId ||
        report.calibrationBlockAssetId === assetId
      )) return true;
    }
    return false;
  };
}

export function createNdtCatalogUsageChecker(
  meReportRepo: MeReportRepository,
  lpReportRepo?: LpReportRepository,
  others: Array<{ listAll?: () => Promise<readonly { referenceNormId: string | null; acceptanceCriteriaId: string | null; couplantId?: string | null }[]> }> = []
) {
  return async (itemId: string): Promise<boolean> => {
    if (meReportRepo.listAll) {
      const reports = await meReportRepo.listAll();
      if (reports.some(report =>
        report.referenceNormId === itemId ||
        report.acceptanceCriteriaId === itemId ||
        report.couplantId === itemId
      )) return true;
    }
    if (lpReportRepo?.listAll) {
      const reports = await lpReportRepo.listAll();
      if (reports.some(report => report.referenceNormId === itemId || report.acceptanceCriteriaId === itemId)) return true;
    }
    for (const repo of others) {
      if (!repo.listAll) continue;
      const reports = await repo.listAll();
      if (reports.some(report => report.referenceNormId === itemId || report.acceptanceCriteriaId === itemId || report.couplantId === itemId)) return true;
    }
    return false;
  };
}

/** Um consumível PM já usado num Relatório PM é inativado, nunca apagado. */
export function createPmConsumableUsageChecker(pmReportRepo: PmReportRepository) {
  return async (consumableId: string): Promise<boolean> => {
    if (!pmReportRepo.listAll) return false;
    const reports = await pmReportRepo.listAll();
    return reports.some(report =>
      report.particles?.consumableId === consumableId ||
      report.contrastPaint?.consumableId === consumableId ||
      report.carrier?.consumableId === consumableId
    );
  };
}

/** Um consumível LP já usado num relatório é inativado, nunca apagado. */
export function createLpConsumableUsageChecker(lpReportRepo: LpReportRepository) {
  return async (consumableId: string): Promise<boolean> => {
    if (!lpReportRepo.listAll) return false;
    const reports = await lpReportRepo.listAll();
    return reports.some(report =>
      report.penetrant?.consumableId === consumableId ||
      report.remover?.consumableId === consumableId ||
      report.developer?.consumableId === consumableId
    );
  };
}

export class BrowserPlatform {
  public equipmentService!: EquipmentService;
  public instrumentService!: InstrumentService;
  public safetyDeviceService!: SafetyDeviceService;
  public inspectionService!: InspectionService;
  public reportService!: ReportService;
  public inspectionBookService!: InspectionBookService;
  public adminService!: AdminService;
  public rdoService!: RdoService;
  public serviceFrontService!: ServiceFrontService;
  public ndtAssetService!: NdtAssetService;
  public ndtEquipmentImportService!: NdtEquipmentImportService;
  public signatureService!: SignatureService;
  public engineeringService!: EngineeringService;
  public projectService!: ProjectService;
  public minutaService!: MinutaService;
  public meReportService!: MeReportService;
  public lpReportService!: LpReportService;
  public pmReportService!: PmReportService;
  public conventionalUsReportService!: ConventionalUsReportService;
  public bscanReportService!: BscanReportService;
  public ndtReportCatalogService!: NdtReportCatalogService;
  public lpConsumableService!: LpConsumableService;
  public pmConsumableService!: PmConsumableService;
  public ndtProcedureService!: NdtProcedureService;
  public ndtCatalogService!: NdtCatalogService;
  public engineeringCalculationService!: EngineeringCalculationService;
  public avrReportService!: AvrReportService;
  /** Revisão e aprovação PLH — mecanismo central (LP, ME, PM; RI listado por leitura). */
  public technicalReviewService!: TechnicalReviewService;
  /** Painel de Engenharia: correções por item, HOLD, corretor ortográfico e o agregador do painel. */
  public technicalCorrectionService!: TechnicalCorrectionService;
  public documentHoldService!: DocumentHoldService;
  public engineeringDashboardService!: EngineeringDashboardService;
  /** Fonte de verdade dos responsáveis (Engenheiro/PLH) — o Projeto. */
  public projectResponsibilityService!: ProjectResponsibilityService;
  public integrityCatalogService!: IntegrityCatalogService;
  /** Book de Inspeção — consolidador documental (RI/END/AVR/Minuta/anexos/PIT), só leitura. */
  public inspectionBookConsolidatorService!: InspectionBookConsolidatorService;
  /** LISTA DE LINHAS de um Sistema de Tubulação — fonte única usada pelo RI, pelo Book e pelo PDF independente ("BAIXAR PDF"). */
  public pipingSystemLineListService!: PipingSystemLineListService;
  /** Documentação e Biblioteca Técnica (Bloco 03): anexar/vincular/versionar/consultar/baixar. */
  public technicalDocumentService!: TechnicalDocumentService;
  /** Importação Inteligente de Sistemas e Tubulações (Bloco 04). */
  public equipmentImportService!: EquipmentImportService;
  /** CONFIGURAÇÕES — fachada com permissão por ato (a única exposta às telas / ao RPC). */
  public systemSettingsService!: SystemSettingsService;
  /** Leitura interna das configurações. NÃO é serviço de RPC. */
  public systemSettingsProvider!: SystemSettingsProvider;
  /** Alocação de número de documento. NÃO é serviço de RPC. */
  public documentNumberingService!: DocumentNumberingService;
  /** STUDIO INSPECT IA — fachada com permissão por ato (a única exposta às telas / ao RPC). */
  public studioDesignService!: StudioDesignService;
  /** Leitura interna da identidade publicada. NÃO é serviço de RPC. */
  public studioDesignProvider!: StudioDesignProvider;
  public dashboardShareService!: DashboardShareService;
  /** INSPECT IA REVIEW — IA oficial da plataforma (substitui o Córtex). */
  public inspectIaReviewService!: InspectIaReviewService;
  /** Acesso pelo token (sem sessão). No modo navegador a página pública chama direto. */
  public dashboardSharePublicService!: DashboardSharePublicService;
  public backupRepository!: InMemoryBackupRepository;
  public backupService!: BackupService;
  public restoreService!: RestoreService;
  public fieldControlService!: FieldControlService;
  public userHomeService!: UserHomeService;
  public imageAnnotationService!: ImageAnnotationService;
  public checklistImageAnnotationService!: ImageAnnotationService;
  public authorizationService: RbacAuthorizationService = new RbacAuthorizationService();

  constructor() {
    const authService = this.authorizationService;

    const equipmentRepo = new InMemoryEquipmentRepository();
    const instrumentRepo = new InMemoryInstrumentRepository();
    const safetyDeviceRepo = new InMemorySafetyDeviceRepository();
    const inspectionRepo = new InMemoryInspectionRepository();
    const checklistRepo = new InMemoryChecklistRepository();
    const reportRepo = new InMemoryIntegrityReportRepository();
    const recommendationRepo = new InMemoryRecommendationRepository();
    const bookRepo = new InMemoryInspectionBookRepository();
    const adminRepo = new InMemoryAdminRepository();
    const identityRepo = new InMemoryIdentityRepository();
    const companyRepo = new InMemoryCompanyRepository();
    const rdoRepo = new InMemoryRdoRepository();
    const ndtAssetRepo = new InMemoryNdtAssetRepository();
    const projectRepo = new InMemoryProjectRepository();
    const minutaRepo = new InMemoryMinutaRepository();
    const meReportRepo = new InMemoryMeReportRepository();
    const lpReportRepo = new InMemoryLpReportRepository();
    const lpConsumableRepo = new InMemoryLpConsumableRepository();
    const ndtProcedureRepo = new InMemoryNdtProcedureRepository();
    const ndtCatalogRepo = new InMemoryNdtCatalogRepository();

    const historyChecker: EquipmentHistoryChecker = createEquipmentHistoryChecker({
      equipmentRepo,
      inspectionRepo,
      checklistRepo,
      reportRepo,
      recommendationRepo,
      instrumentRepo,
      safetyDeviceRepo
    });

    this.equipmentService = new EquipmentService(equipmentRepo, authService, mockAudit, historyChecker, projectRepo);
    this.pipingSystemLineListService = new PipingSystemLineListService(this.equipmentService, {
      listSafetyDevices: (context, filters) => this.safetyDeviceService.listSafetyDevices(context, filters),
      listInstruments: (context, filters) => this.instrumentService.listInstruments(context, filters)
    });
    this.instrumentService = new InstrumentService(instrumentRepo, authService, mockAudit);
    this.safetyDeviceService = new SafetyDeviceService(safetyDeviceRepo, authService, mockAudit);
    this.inspectionService = new InspectionService(inspectionRepo, checklistRepo, this.equipmentService, identityRepo, recommendationRepo, reportRepo);
    this.reportService = new ReportService(reportRepo, inspectionRepo, recommendationRepo, checklistRepo, mockAudit, this.equipmentService, identityRepo, this.instrumentService, this.safetyDeviceService, projectRepo);
    this.inspectionBookService = new InspectionBookService({
      bookRepo,
      inspectionRepo,
      reportRepo,
      checklistRepo,
      recommendationRepo,
      equipmentRepo,
    });
    this.adminService = new AdminService(adminRepo, identityRepo, companyRepo, authService, mockAudit, projectRepo, createUserHistoryChecker(minutaRepo));
    this.rdoService = new RdoService(
      rdoRepo,
      adminRepo,
      identityRepo,
      undefined,
      async (projectId: string) => {
        const project = await projectRepo.getProject(projectId);
        return project ? { code: project.code, name: project.name } : null;
      },
      // Hierarquia OS → Projeto → Unidade/Área, resolvida pela Sub-OS no RDO.
      async (clientId: string | null) => projectRepo.listProjects(clientId ? { clientId } : undefined),
      async (clientId: string | null) => projectRepo.listUnitAreas(clientId ? { clientId } : undefined)
    );
    this.serviceFrontService = new ServiceFrontService(adminRepo, projectRepo, rdoRepo, authService);
    const pmReportRepo = new InMemoryPmReportRepository();
    const uscReportRepo = new InMemoryConventionalUsReportRepository();
    const bscanReportRepo = new InMemoryBscanReportRepository();
    this.ndtAssetService = new NdtAssetService(ndtAssetRepo, mockAudit as any, createNdtAssetUsageChecker(meReportRepo, pmReportRepo, uscReportRepo, bscanReportRepo), createNdtAssetUsageLister({ me: meReportRepo, pm: pmReportRepo, usc: uscReportRepo, bscan: bscanReportRepo }), new InMemoryNdtAssetAllocationRepository(), async (projectId, clientId) => {
      const project = await projectRepo.getProject(projectId);
      return !!project && String(project.clientId) === clientId;
    });
    this.ndtEquipmentImportService = new NdtEquipmentImportService(this.ndtAssetService);
    this.signatureService = new SignatureService(mockAudit as any);
    this.minutaService = new MinutaService(minutaRepo, mockAudit);
    this.meReportService = new MeReportService(meReportRepo, identityRepo);
    this.lpReportService = new LpReportService(lpReportRepo, identityRepo, async (inspectionId: string) => {
      const inspection = await inspectionRepo.findById({ activeClientId: undefined } as any, inspectionId as any);
      return inspection ? { id: inspection.id, equipmentId: String(inspection.equipmentId), clientId: String(inspection.clientId) } : null;
    });
    this.lpConsumableService = new LpConsumableService(lpConsumableRepo, createLpConsumableUsageChecker(lpReportRepo));
    // Consumíveis PM: catálogo global; um consumível usado num Relatório PM não é apagado.
    this.pmConsumableService = new PmConsumableService(new InMemoryPmConsumableRepository(), createPmConsumableUsageChecker(pmReportRepo));
    const inspectionLookup = async (inspectionId: string) => {
      const inspection = await inspectionRepo.findById({ activeClientId: undefined } as any, inspectionId as any);
      return inspection ? { id: inspection.id, equipmentId: String(inspection.equipmentId), clientId: String(inspection.clientId) } : null;
    };
    this.pmReportService = new PmReportService(pmReportRepo, identityRepo, inspectionLookup);
    this.conventionalUsReportService = new ConventionalUsReportService(uscReportRepo, identityRepo, inspectionLookup);
    this.bscanReportService = new BscanReportService(bscanReportRepo, identityRepo, inspectionLookup);
    // M21: bloqueio no backend antes da validação, assinatura e aprovação final.
    const assetUsageValidator = createNdtReportAssetValidator(this.ndtAssetService);
    this.meReportService.attachNdtAssetValidator(assetUsageValidator);
    this.pmReportService.attachNdtAssetValidator(assetUsageValidator);
    this.conventionalUsReportService.attachNdtAssetValidator(assetUsageValidator);
    this.bscanReportService.attachNdtAssetValidator(assetUsageValidator);
    // Central de Relatórios END (Bloco 05): só leitura, reaproveita os 4 serviços acima.
    this.ndtReportCatalogService = new NdtReportCatalogService({
      meReportService: this.meReportService,
      lpReportService: this.lpReportService,
      pmReportService: this.pmReportService,
      conventionalUsReportService: this.conventionalUsReportService,
      bscanReportService: this.bscanReportService
    });
    this.reportService.attachNdtServices({
      meReportRepo,
      lpReportRepo,
      pmReportRepo,
      uscReportRepo,
      bscanReportRepo,
      meReportService: this.meReportService,
      lpReportService: this.lpReportService,
      pmReportService: this.pmReportService,
      conventionalUsReportService: this.conventionalUsReportService,
      bscanReportService: this.bscanReportService
    });
    this.ndtProcedureService = new NdtProcedureService(
      ndtProcedureRepo,
      createNdtProcedureUsageChecker(meReportRepo, lpReportRepo, [pmReportRepo, uscReportRepo, bscanReportRepo])
    );
    this.ndtCatalogService = new NdtCatalogService(
      ndtCatalogRepo,
      createNdtCatalogUsageChecker(meReportRepo, lpReportRepo, [pmReportRepo, uscReportRepo, bscanReportRepo])
    );
    this.integrityCatalogService = new IntegrityCatalogService(new InMemoryIntegrityCatalogRepository());
    this.engineeringCalculationService = new EngineeringCalculationService(
      new InMemoryCalculationRepository(),
      equipmentRepo,
      meReportRepo,
      this.integrityCatalogService
    );
    // AVR: fontes de espessura SÓ pelos adaptadores ME e B-Scan; taxa e vida
    // pelo mesmo EngineeringCalculationService da calculadora API 570. O
    // adaptador B-Scan fica vazio até existir um Relatório B-Scan no app.
    this.avrReportService = new AvrReportService(
      new InMemoryAvrReportRepository(),
      this.engineeringCalculationService,
      [new MeAvrSourceAdapter(meReportRepo), new BscanAvrSourceAdapter()],
      identityRepo,
      recommendationRepo,
      mockAudit
    );
    this.engineeringService = new EngineeringService(this.reportService, this.signatureService, authService, this.minutaService, recommendationRepo, mockAudit);
    this.projectService = new ProjectService(projectRepo, authService, identityRepo, mockAudit);

    // Revisão e aprovação PLH — um único ciclo para LP, ME e PM; cada módulo
    // só delega validação/snapshot e consulta o bloqueio pelos ganchos. O RI
    // mantém o seu workflow (Workspace de Engenharia) e entra na fila por leitura.
    this.technicalReviewService = new TechnicalReviewService(new InMemoryTechnicalReviewRepository(), identityRepo, mockAudit);
    // M21: antes de PLH_APPROVED, checar os instrumentos do próprio documento.
    this.technicalReviewService.attachNdtAssetApprovalGate(async (context, type, id) => {
      let report: object | null = null;
      if (type === "ME") report = await meReportRepo.findById(context, id);
      else if (type === "PM") report = await pmReportRepo.findById(context, id);
      else if (type === "USC") report = await uscReportRepo.findById(context, id);
      else if (type === "BSCAN") report = await bscanReportRepo.findById(context, id);
      else return;
      if (!report) throw new AppError(ErrorCode.NotFound, "Relatório END não encontrado.");
      const issues = await assetUsageValidator(context, report);
      if (issues.length) throw new AppError(ErrorCode.Validation, "Instrumentos END não liberados", {
        safeMessage: issues.join(" ")
      });
    });
    this.technicalReviewService.registerAdapter(lpReviewAdapter(this.lpReportService));
    this.technicalReviewService.registerAdapter(meReviewAdapter(this.meReportService, {
      getEquipment: (context, equipmentId) => this.equipmentService.getEquipment(context, equipmentId) as any
    }));
    this.technicalReviewService.registerAdapter(pmReviewAdapter(this.pmReportService));
    this.technicalReviewService.registerAdapter(uscReviewAdapter(this.conventionalUsReportService));
    this.technicalReviewService.registerAdapter(bscanReviewAdapter(this.bscanReportService));
    const reviewHooks = this.technicalReviewService.hooks();
    this.lpReportService.attachReviewHooks(reviewHooks);
    this.meReportService.attachReviewHooks(reviewHooks);
    this.pmReportService.attachReviewHooks(reviewHooks);
    this.conventionalUsReportService.attachReviewHooks(reviewHooks);
    this.bscanReportService.attachReviewHooks(reviewHooks);

    // Painel de Engenharia: correções por item (deep link ao Inspetor), HOLD e corretor ortográfico.
    this.technicalCorrectionService = new TechnicalCorrectionService(new InMemoryTechnicalCorrectionRepository(), identityRepo, mockAudit);
    this.technicalReviewService.attachCorrectionService(this.technicalCorrectionService);
    this.documentHoldService = new DocumentHoldService(new InMemoryDocumentHoldRepository(), identityRepo, mockAudit);
    const holdHooks = this.documentHoldService.hooks();
    this.lpReportService.attachHoldHooks(holdHooks);
    this.meReportService.attachHoldHooks(holdHooks);
    this.pmReportService.attachHoldHooks(holdHooks);
    this.conventionalUsReportService.attachHoldHooks(holdHooks);
    this.bscanReportService.attachHoldHooks(holdHooks);
    // Gate de servidor da cascata Inspetor → Sub-OS do ME: a tela filtra, mas
    // quem recusa um par incompatível é o serviço.
    // Abrir um Equipamento pelo id: a visibilidade segue o ACESSO do usuário
    // ao Cliente do ativo, não o Cliente selecionado na sessão. Sem este
    // verificador o serviço mantém o escopo estrito de antes.
    this.equipmentService.attachClientAccessChecker(async (context, clientId) => {
      try { await this.adminService.assertUserHasAccessToClient(context, clientId); return true; }
      catch { return false; }
    });
    this.equipmentService.attachAuthorizedClientIdsResolver(async context =>
      (await this.adminService.listAuthorizedClients(context)).map(client => client.id)
    );
    // INSTRUMENTO e PSV: "Todos os Clientes" = Clientes autorizados; Projeto,
    // Unidade/Área e Equipamento do cadastro conferidos contra o Cliente.
    const technicalAssetHierarchy: TechnicalAssetHierarchyLookup = {
      getProject: projectId => projectRepo.getProject(projectId),
      getUnitArea: unitAreaId => projectRepo.getUnitArea(unitAreaId),
      getEquipment: (clientId, equipmentId) => equipmentRepo.getEquipment(clientId, equipmentId)
    };
    for (const service of [this.instrumentService, this.safetyDeviceService]) {
      service.attachHierarchyLookup(technicalAssetHierarchy);
      service.attachAuthorizedClientIdsResolver(async context =>
        (await this.adminService.listAuthorizedClients(context)).map(client => client.id)
      );
    }
    // PROJETOS e UNIDADES/ÁREAS: todo resultado é a interseção com os Clientes
    // AUTORIZADOS (política oficial). Sem `ClientAccess` não há Cliente visível.
    this.projectService.attachAuthorizedClientIdsResolver(async context =>
      context.permissions.has(PermissionCode.ClientAccess)
        ? new Set((await this.adminService.listAuthorizedClients(context)).map(client => String(client.id)))
        : new Set<string>()
    );
    // FRENTES DE SERVIÇO: mesma regra de "Todos os Clientes" e o nome real do
    // Cliente na coluna da listagem.
    this.serviceFrontService.attachAuthorizedClientIdsResolver(async context =>
      (await this.adminService.listAuthorizedClients(context)).map(client => client.id)
    );
    this.serviceFrontService.attachClientNameLookup(async () =>
      new Map((await identityRepo.listClients()).map(client => [String(client.id), client.name]))
    );
    this.meReportService.attachSubOsResolver((context, _clientId, workOrderId) =>
      this.adminService.listSubWorkOrdersForDocument(context, workOrderId));
    // MÉTODO A — RESULTADO APROXIMADO: de onde `saveDraft` lê Vsa, k e a
    // Temperatura de Referência do Procedimento vinculado ao relatório.
    this.meReportService.attachNdtProcedureRepo(ndtProcedureRepo);
    this.meReportService.attachAuditRecorder(mockAudit);
    // Engenheiro e PLH vêm do Projeto (Gestão → Projetos) — nunca seleção manual nos relatórios.
    this.projectResponsibilityService = new ProjectResponsibilityService(projectRepo, identityRepo);
    this.technicalReviewService.attachResponsibility(this.projectResponsibilityService);
    this.technicalReviewService.attachHoldService(this.documentHoldService);
    this.avrReportService.attachPanelServices({ responsibility: this.projectResponsibilityService, holds: this.documentHoldService, corrections: this.technicalCorrectionService });

    // Descrição do RI para a fila, o workspace e a Minuta — resolvida uma vez, nunca redigitada.
    const describeIntegrityReport = async (context: any, report: any): Promise<IntegrityReportDescription> => {
      const [inspection, clients, professionals] = await Promise.all([
        inspectionRepo.findById(context, report.inspectionId).catch(() => null),
        identityRepo.listClients().catch(() => []),
        identityRepo.listProfessionals().catch(() => [])
      ]);
      const equipment: any = inspection ? await equipmentRepo.getEquipment(report.clientId, inspection.equipmentId).catch(() => null) : null;
      const client = clients.find((c: any) => c.id === report.clientId);
      const name = (id: string | null) => (id ? professionals.find((p: any) => String(p.id) === String(id))?.name || "" : "");
      const insp: any = inspection;
      let project = insp?.projectName || equipment?.projectName || "";
      let projectEngineerId: string | null = null;
      let projectPlhId: string | null = null;
      let unitArea = insp?.unitAreaName || equipment?.unitAreaName || equipment?.location || "";
      const projectId = insp?.projectId || equipment?.projectId || null;
      const unitAreaId = insp?.unitAreaId || equipment?.unitAreaId || equipment?.areaId || null;
      try {
        if (projectId) {
          const proj: any = (await projectRepo.listProjects()).find((x: any) => x.id === projectId);
          if (proj) { project = [proj.code, proj.name].filter(Boolean).map(String).join(" - ") || project; projectEngineerId = proj.engineerProfessionalId || null; projectPlhId = proj.plhProfessionalId || null; }
        }
        if (unitAreaId) {
          const area: any = (await projectRepo.listUnitAreas()).find((x: any) => x.id === unitAreaId);
          if (area) unitArea = [area.code, area.name].filter(Boolean).map(String).join(" - ") || unitArea;
        }
      } catch { /* cadastro de projetos indisponível: fica o que a inspeção/equipamento já dizem */ }
      const typeMap: Record<string, string> = { PIPING_LINE: "TUBULAÇÃO", PIPING_SYSTEM: "SISTEMA DE TUBULAÇÃO", PRESSURE_VESSEL: "VASO DE PRESSÃO", STORAGE_TANK: "TANQUE DE ARMAZENAMENTO", HEAT_EXCHANGER: "TROCADOR DE CALOR", BOILER: "CALDEIRA", FURNACE: "FORNO", OTHER: "EQUIPAMENTO" };
      return {
        clientId: String(report.clientId),
        client: client ? ((client as any).fantasyName || client.name || "") : String(report.clientId),
        projectId,
        project,
        unitArea,
        equipmentId: insp?.equipmentId ? String(insp.equipmentId) : (equipment?.id ? String(equipment.id) : null),
        tag: equipment?.tag || "",
        equipmentType: typeMap[equipment?.equipmentType] || equipment?.equipmentTypeDisplay || equipment?.equipmentType || "",
        osNumber: insp?.osNumber || equipment?.osNumber || "",
        subOsNumber: insp?.subOsNumber || "",
        inspectorProfessionalId: report.inspectorId ? String(report.inspectorId) : (insp?.inspectorId ? String(insp.inspectorId) : null),
        inspectorName: name(report.inspectorId ? String(report.inspectorId) : (insp?.inspectorId ? String(insp.inspectorId) : null)),
        // Responsáveis pelo Projeto (fonte de verdade) — o EngineeringService sobrepõe o snapshot congelado depois do envio.
        engineerProfessionalId: projectEngineerId,
        engineerName: name(projectEngineerId),
        plhProfessionalId: projectPlhId || (report.engineerPlhId ? String(report.engineerPlhId) : null),
        plhName: name(projectPlhId || (report.engineerPlhId ? String(report.engineerPlhId) : null)),
        inspectionDate: insp?.startDate || "",
        projectCode: equipment?.projectCode || equipment?.constructionCode || equipment?.properties?.constructionCode || "",
        fabricationCode: equipment?.fabricationCode || equipment?.properties?.fabricationCode || "",
        fabricationStandard: equipment?.fabricationStandard || equipment?.projectStandard || equipment?.properties?.designConstructionCode || equipment?.inServiceInspectionCode || "",
        // Melhoria 012 (Minuta definitiva): SPEC e Sistema do Equipamento real — nunca inventados quando ausentes.
        materialSpec: equipment?.spec || equipment?.materialSpecification || equipment?.properties?.spec || "",
        // P0-03: quando o equipamento É um Sistema de Tubulação (PIPING_SYSTEM),
        // o campo `system` pode estar vazio — o próprio tag/name identifica o sistema.
        systemName: (equipment?.system && equipment.system !== 'NÃO APLICÁVEL' && equipment.system !== 'NÃO INFORMADO')
          ? equipment.system
          : (equipment?.equipmentType === 'PIPING_SYSTEM' ? (equipment?.tag || equipment?.name || "") : "")
      };
    };
    // Aba END (Ensaios) do Workspace do RI: relatórios END do contexto (Cliente/Projeto/Unidade/Equipamento) com o estado na Engenharia.
    const listNdtForContext = async (context: any, scope: { clientId: string; projectId: string | null; unitAreaId: string | null; equipmentId: string | null; equipmentTag: string }): Promise<EngineeringNdtRow[]> => {
      const sources: Array<{ type: "LP" | "ME" | "PM" | "USC" | "BSCAN"; svc: any; route: string }> = [
        { type: "LP", svc: this.lpReportService, route: "/ndt/lp" }, { type: "ME", svc: this.meReportService, route: "/ndt/thickness" },
        { type: "PM", svc: this.pmReportService, route: "/ndt/pm" }, { type: "USC", svc: this.conventionalUsReportService, route: "/ndt/us-conventional" },
        { type: "BSCAN", svc: this.bscanReportService, route: "/ndt/bscan" }
      ];
      const rows: EngineeringNdtRow[] = [];
      for (const s of sources) {
        let list: any[] = [];
        try { list = [...await s.svc.listByClient(context, scope.clientId)]; } catch { list = []; }
        for (const r of list) {
          const sameEquipment = (scope.equipmentId && String(r.equipmentId || "") === String(scope.equipmentId)) || (scope.equipmentTag && (r.equipmentTag || "").toUpperCase() === scope.equipmentTag.toUpperCase());
          if (!sameEquipment) continue;
          if (scope.projectId && r.projectId && String(r.projectId) !== String(scope.projectId)) continue;
          if (scope.unitAreaId && r.unitAreaId && String(r.unitAreaId) !== String(scope.unitAreaId)) continue;
          let review: any = null;
          try { review = await this.technicalReviewService.getDocumentReview(context, s.type, r.id); } catch { review = null; }
          const workflow = review ? workflowStatusOfReview({ status: review.status, cycle: review.cycle, correctedAt: review.correctedAt, signedAt: review.signedAt }) : (r.status === "SIGNED" ? "ASSINADO" : "EM_ELABORACAO");
          rows.push({ documentType: s.type, typeLabel: TECHNICAL_DOCUMENT_TYPE_LABELS[s.type], documentId: r.id, documentNumber: r.reportNumber, revision: r.revision, tag: r.equipmentTag || "", testDate: r.testDate || "", inspectorName: r.inspectorName || r.createdBy || "", status: workflow, statusLabel: ENGINEERING_WORKFLOW_STATUS_LABELS[workflow], submittedAt: review?.submittedAt ?? null, reviewRoute: review ? `/engineering/review/${review.id}` : null, documentRoute: `${s.route}/${r.id}` });
        }
      }
      return rows.sort((a, b) => (b.submittedAt || "").localeCompare(a.submittedAt || "") || a.documentNumber.localeCompare(b.documentNumber));
    };
    // Substituição do PLH do Cliente: o AdminService não conhece o ciclo de
    // revisão, então recebe por aqui a transferência dos pendentes.
    this.adminService.attachPlhReassignment({
      listReassignablePendingForClient: (context, clientId, current) =>
        this.technicalReviewService.listReassignablePendingForClient(context, clientId, current),
      reassignPendingToPlh: (context, clientId, newPlhId, newPlhName, current) =>
        this.technicalReviewService.reassignPendingToPlh(context, clientId, newPlhId, newPlhName, current)
    });
    this.engineeringService.attachPanelServices({
      corrections: this.technicalCorrectionService,
      holds: this.documentHoldService,
      responsibility: this.projectResponsibilityService,
      lookups: {
        describe: describeIntegrityReport,
        listAvrs: (context, _clientId, equipmentId) => (equipmentId ? this.avrReportService.listByEquipment(context, equipmentId) : Promise.resolve([])),
        listNdtForContext
      }
    });
    this.technicalReviewService.registerExternalSource(integrityReviewSource({
      listReports: (context) => this.reportService.listReports(context),
      describe: async (context, report) => {
        const d = await describeIntegrityReport(context, report);
        const frozen: any = (report as any).responsibility;
        // P0-02 V4 Etapa 10: review source deve considerar TODAS as Minutas da inspeção.
        const minutas = await this.minutaService.listMinutasByInspection(context, String(report.inspectionId)).catch(() => []);
        const aggregatedStatus = minutas.length === 0 ? null : minutas.some(m => !['CONFORME', 'ANÁLISE CONCLUÍDA', 'APROVADA'].includes(m.status)) ? minutas.find(m => !['CONFORME', 'ANÁLISE CONCLUÍDA', 'APROVADA'].includes(m.status))?.status ?? 'PENDENTE' : 'APROVADA';
        return { client: d.client, project: d.project, unitArea: d.unitArea, tag: d.tag, osNumber: d.osNumber, subOsNumber: d.subOsNumber, elaboratedBy: d.inspectorName, plhName: frozen?.plhName || d.plhName, equipmentType: d.equipmentType, inspectionDate: d.inspectionDate, engineerProfessionalId: frozen?.engineerProfessionalId ?? d.engineerProfessionalId, engineerName: frozen?.engineerName || d.engineerName, minutaStatus: aggregatedStatus };
      }
    }));
    // AVR PARA ANÁLISE: toda AVR do contexto entra na fila da Engenharia com ANALISAR; o Engenheiro responsável vem do Projeto.
    this.technicalReviewService.registerExternalSource(avrReviewSource({
      listAvrs: (context) => this.avrReportService.listForEngineering(context, context.activeClientId || null),
      engineerOf: async (context, avr) => {
        const r = await this.projectResponsibilityService.resolveProjectEngineer(context, avr.projectId);
        return r.responsible ? { id: r.responsible.professionalId, name: r.responsible.name } : { id: avr.engineerResponsible?.professionalId ? String(avr.engineerResponsible.professionalId) : null, name: avr.engineerResponsible?.name || "" };
      }
    }));
    this.engineeringDashboardService = new EngineeringDashboardService(this.technicalReviewService, this.technicalCorrectionService, this.documentHoldService, this.minutaService);

    // Documentação e Biblioteca Técnica (Bloco 03): anexar/vincular/versionar/consultar.
    // Arquivos (fotos, assinaturas, logos, fundo do login): IndexedDB, durável.
    // Sem IndexedDB (ambiente de teste), só memória.
    const documentStorage = typeof indexedDB !== "undefined"
      ? new DurableDocumentStorageService(new IndexedDbBlobPersistence(), localStorageLegacyBlobs())
      : new InMemoryDocumentStorageService();
    this.technicalDocumentService = new TechnicalDocumentService(
      new InMemoryTechnicalDocumentRepository(),
      documentStorage,
      this.equipmentService,
      mockAudit
    );

    // Editor de imagem: usa o MESMO armazenamento de blobs da Biblioteca
    // Técnica — a versão anotada é sempre um arquivo novo, a original fica.
    this.imageAnnotationService = new ImageAnnotationService(documentStorage, mockAudit);
    // Fotos do Checklist: quem edita o Checklist edita a foto dele. A regra
    // é a do módulo (comando 05, item 31) — não se cria permissão nova.
    this.checklistImageAnnotationService = new ImageAnnotationService(documentStorage, mockAudit, {
      view: PermissionCode.ChecklistRead,
      edit: PermissionCode.ChecklistUpdate
    });

    // Importação Inteligente de Sistemas e Tubulações (Bloco 04): grava sempre
    // via EquipmentService.createEquipment, nunca direto no repositório.
    this.equipmentImportService = new EquipmentImportService(
      this.equipmentService,
      new InMemoryImportBatchRepository(),
      // Reversão compensatória: o repositório não oferece transação real, então
      // um lote que falha é desfeito registro a registro, em ordem inversa.
      { remove: (clientId, equipmentId) => equipmentRepo.deleteEquipment(clientId as any, equipmentId as any) },
      mockAudit
    );

    // Controle de Campo: no modo local o avanço fica só nesta sessão. A
    // fonte multiusuário oficial é a composição de backend, que usa o
    // repositório SQLite central.
    // M16A HOME-FINAL-01: injeção de dependências para dados operacionais reais.
    // BrowserPlatform usa InMemoryIdentityRepository que não implementa ProfessionalRepository;
    // passamos null e o serviço gracefully retorna dados vazios em ambiente browser-only.
    // Dados reais vêm via RPC do BackendPlatform (que injeta dataCore.professionals).
    this.fieldControlService = new FieldControlService(
      new InMemoryFieldControlRepository(),
      this.equipmentService,
      mockAudit,
      identityRepo
    );
    this.userHomeService = new UserHomeService(
      undefined,
      this.reportService?.reportRepo,
      this.fieldControlService,
      this.rdoService
    );

    // Book de Inspeção: consolidador documental só de leitura — nunca cria
    // relatório, minuta, isométrico, lista de linhas, folha de dados,
    // fluxograma ou PIT; localiza e organiza o que os módulos oficiais já têm
    // (inclui o que foi anexado pela Biblioteca Técnica do Bloco 03).
    this.inspectionBookConsolidatorService = new InspectionBookConsolidatorService({
      equipmentService: this.equipmentService,
      reportService: this.reportService,
      minutaService: this.minutaService,
      avrReportService: this.avrReportService,
      inspectionBookService: this.inspectionBookService,
      projectService: this.projectService,
      technicalDocumentService: this.technicalDocumentService,
      ndt: { LP: this.lpReportService, ME: this.meReportService, PM: this.pmReportService, USC: this.conventionalUsReportService, BSCAN: this.bscanReportService }
    });

    // Clean mock flags
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('inspect_seeded');
      localStorage.removeItem('auto_seeder_v1');
    }

    // Global technical catalog: the approved starting couplants must exist for
    // every client, independent of any seeded operational data.
    this.ndtCatalogService
      .ensureDefaultCouplants({ userId: 'system', permissions: new Set([PermissionCode.NdtRegistryRead, PermissionCode.NdtRegistryCreate]) } as any)
      .catch(console.error);

    // CONFIGURAÇÕES → ligada por último: precisa dos serviços que a consomem.
    const settingsRepo = new InMemorySystemSettingsRepository();
    const settingsSecrets = new UnavailableSecretStore("Modo navegador (teste local): não há servidor para guardar segredo com segurança, e ele não é guardado no navegador. Cadastre o segredo no ambiente com servidor.");
    const settingsProbe = browserSettingsProbe;
    const settingsAudit = mockAudit;
    const settings = wireSystemSettings(settingsRepo, settingsSecrets, settingsProbe, settingsAudit, {
      numberedServices: [this.reportService, this.meReportService, this.lpReportService, this.pmReportService, this.conventionalUsReportService, this.bscanReportService, this.avrReportService, this.minutaService],
      endServices: [this.pmReportService, this.conventionalUsReportService, this.bscanReportService],
      adminService: this.adminService,
      importService: this.equipmentImportService,
      lookups: {
        clientCode: async id => (await identityRepo.getClient(id as any))?.code ?? null,
        projectCode: async id => (await projectRepo.getProject(id))?.code ?? null,
        unitCode: async id => (await projectRepo.getUnitArea(id))?.code ?? null,
        userName: async id => (await identityRepo.getUser(id as any))?.name ?? null
      },
      databaseReset: {
        adminAuth: async (identifier: string, password: string) => {
          return this.adminService.authenticateByCredentials(identifier, password);
        },
        handler: async (context: RequestContext, _reason?: string) => {
          systemResetService.executeControlledReset({ executedBy: String(context.userId || "usr_admin") });
          return {
            success: true,
            backupPath: "localStorage_snapshot",
            backupSizeBytes: 0,
            deletedCounts: {},
            executedAt: new Date().toISOString(),
            executedBy: String(context.userId || "usr_admin")
          };
        }
      }
    });
    this.systemSettingsService = settings.service;
    this.systemSettingsProvider = settings.provider;
    this.documentNumberingService = settings.numbering;

    // STUDIO INSPECT IA — o visual publicado entra nos modelos documentais montados a partir daqui.
    const studio = wireStudioDesign(new InMemoryStudioDesignRepository(), documentStorage, settingsAudit, {
      documentServices: [this.meReportService, this.lpReportService, this.pmReportService, this.conventionalUsReportService, this.bscanReportService, this.avrReportService],
      userName: async id => (await identityRepo.getUser(id as any))?.name ?? null,
      integrityReports: this.reportService,
      minutaEngineering: this.engineeringService,
      inspectionBookService: this.inspectionBookService,
      inspectionBookConsolidator: this.inspectionBookConsolidatorService
    });
    this.studioDesignService = studio.service;
    this.studioDesignProvider = studio.provider;

    // PAINEL — link compartilhado (Correção 9). Só o serviço autenticado vai ao
    // /api/rpc; o acesso público pelo token tem endpoint próprio.
    const dashboardShare = wireDashboardShare(new InMemoryDashboardShareRepository(), this, settingsAudit, {
      getUser: async id => (await identityRepo.getUser(id as any)) as any,
      clientName: async id => (await identityRepo.getClient(id as any))?.name ?? null,
      assertClientAccess: (context, clientId) => this.adminService.assertUserHasAccessToClient(context, clientId)
    });
    this.dashboardShareService = dashboardShare.service;
    this.dashboardSharePublicService = dashboardShare.publicService;

    // INSPECT IA REVIEW — revisão de todos os relatórios pelo MESMO orquestrador,
    // e revisão IA obrigatória antes de enviar ao PLH.
    this.inspectIaReviewService = wireInspectIaReview(new InMemoryAIReviewRepository(), this as never, settingsAudit,
      async id => (await identityRepo.getUser(id as any))?.name ?? null).service;

    // BACKUP E RESTAURAÇÃO — Ferramenta Central de Backup e Recuperação
    const backupRepo = new InMemoryBackupRepository();
    const backupCollectors = createDefaultBackupCollectors({
      equipmentRepo,
      instrumentRepo,
      safetyDeviceRepo,
      inspectionRepo,
      checklistRepo,
      reportRepo,
      recommendationRepo,
      ndtAssetRepo,
      ndtProcedureRepo,
      ndtCatalogRepo,
      minutaRepo,
      projectRepo,
      adminRepo,
      identityRepo,
      companyRepo
    });
    this.backupRepository = backupRepo;
    this.backupService = new BackupService(backupRepo, authService, mockAudit, backupCollectors);
    this.restoreService = new RestoreService(backupRepo, this.backupService, authService, mockAudit);

    runAutoSeeder(this).catch(console.error);
  }
}

const basePlatform = new BrowserPlatform();

/**
 * Valores de `VITE_APP_ENV` que ligam o modo backend (tudo via /api/rpc).
 * "homologation" é o valor real de Homologação (definido pela Railway, fora
 * deste repositório). "local" é o equivalente para rodar `infra/server.js`
 * apontando para um SQLite LOCAL (`npm run start:local`) — mesma rota de
 * código, mesmo /api/rpc, único servidor real que muda é o de destino do
 * fetch; nenhum dos dois nunca aparece sozinho sem um servidor de verdade
 * do outro lado (sem isso, o app cairia para chamadas HTTP que 404iam).
 *
 * Lido de `__APP_BACKEND_ENV__` (substituição textual via `define` no
 * vite.config.ts), não de `import.meta.env.VITE_APP_ENV` diretamente: neste
 * bundle (muitos import() dinâmicos, muitos chunks) o objeto que o Vite gera
 * para `import.meta.env` acaba duplicado por chunk, e o chunk onde este
 * arquivo cai recebia uma cópia VAZIA — `IS_BACKEND_MODE` nunca ligava,
 * mesmo com a variável de ambiente corretamente definida no build. `define`
 * troca o identificador antes do code-splitting, então todo chunk recebe o
 * mesmo literal.
 */
declare const __APP_BACKEND_ENV__: string;
const BACKEND_MODE_VALUES = new Set(['production', 'homologation', 'local']);
// M16A HOME-FINAL-04: fallback para window.__APP_BACKEND_ENV__ quando o
// bundle foi compilado sem VITE_APP_ENV (build padrão). O server.js local
// injeta esse valor via <script> antes do app carregar, permitindo que o
// proxy RPC seja ativado mesmo sem rebuild com variável de ambiente.
const resolvedBackendEnv = (typeof __APP_BACKEND_ENV__ !== 'undefined' && __APP_BACKEND_ENV__)
  ? __APP_BACKEND_ENV__
  : (typeof window !== 'undefined' && (window as any).__APP_BACKEND_ENV__) || '';
const isRpc = typeof process !== 'undefined' && (process.env['APP_ENV'] === 'homologation' || process.env['APP_ENV'] === 'production') ? false : (BACKEND_MODE_VALUES.has(resolvedBackendEnv));

/**
 * TRUE  → modo backend (Homologação OU local-com-servidor): todo serviço
 *         trafega por /api/rpc e a autenticação é a do servidor (/api/login,
 *         /api/session, /api/logout).
 * FALSE → teste local sem backend (`npm run dev`, só Vite): a plataforma
 *         roda no próprio navegador (in-memory + localStorage) e a
 *         autenticação é local, pelo mesmo repositório do cadastro de
 *         Usuários. Continua sendo o padrão — nada aqui muda esse caminho.
 * Único lugar que decide isso — a camada de autenticação importa esta
 * constante em vez de reavaliar `import.meta.env` por conta própria.
 */
export const IS_BACKEND_MODE = isRpc;

/**
 * Métodos LOCAL_SYNC: executam localmente e sincronamente no próprio navegador
 * (reutilizando a instância local de basePlatform), sem atravessar /api/rpc.
 *
 * Requisitos:
 * 1. São determinísticos / puros;
 * 2. Não acessam banco nem filesystem;
 * 3. Não dependem de sessão server-side nem mutam estado persistente;
 * 4. Retornam resultado imediato e síncrono (não retornam Promise).
 */
export const LOCAL_SYNC_METHODS = new Set([
  // Painel de Engenharia (filtragem e ordenação puras sobre registros já em memória)
  'engineeringDashboardService.filter',
  'engineeringDashboardService.sort',
  'engineeringDashboardService.countsOf',
  'engineeringDashboardService.matchesCard',

  // Cálculos de Engenharia (prévia matemática síncrona de fórmulas)
  'engineeringCalculationService.previewAsmeB313',
  'engineeringCalculationService.previewApi570',
  'engineeringCalculationService.previewNace',

  // RDO Helper
  'rdoService.buildConsolidatedNumber',

  // Helpers de relatório END / Validações síncronas de formulário
  'pmReportService.newReport',
  'pmReportService.normalize',
  'pmReportService.validateCompleteness',
  'conventionalUsReportService.newReport',
  'conventionalUsReportService.normalize',
  'conventionalUsReportService.validateCompleteness',
  'bscanReportService.newReport',
  'bscanReportService.normalize',
  'bscanReportService.validateCompleteness',
  'bscanReportService.modelOf',
  'bscanReportService.photoCaption',
  'bscanReportService.drawingCaption',

  // Helpers de domínio AVR
  'avrReportService.suggestionFor',
  'avrReportService.historyStateFor',
  'avrReportService.canDelete',
  'avrReportService.timelineOf',
  'avrReportService.inCorrection',
  'avrReportService.statusAfterEdit',
  'avrReportService.editableFingerprint',
  'avrReportService.calculationInputsChanged',

  // Technical Review & Hold helpers síncronos
  'technicalReviewService.supportedTypes',
  'technicalReviewService.adapterFor',
  'technicalReviewService.rowOf',
  'technicalReviewService.timelineOf',
  'technicalCorrectionService.sectionsFor',
  'documentHoldService.holdRoute',
  'documentHoldService.typeLabel',
]);

export function uint8ArrayToBase64(bytes: Uint8Array): string {
  if (typeof Buffer !== "undefined") {
    return Buffer.from(bytes.buffer, bytes.byteOffset, bytes.byteLength).toString("base64");
  }
  let binary = "";
  const len = bytes.byteLength;
  const chunkSize = 0x8000;
  for (let i = 0; i < len; i += chunkSize) {
    const chunk = bytes.subarray(i, Math.min(i + chunkSize, len));
    binary += String.fromCharCode.apply(null, chunk as unknown as number[]);
  }
  return btoa(binary);
}

/**
 * Codifica recursivamente qualquer ArrayBuffer ou TypedArray presente nos argumentos RPC
 * para o formato binário canônico { __rpcType: "binary", encoding: "base64", data: string }.
 */
function base64ToUint8Array(base64: string): Uint8Array {
  if (typeof Buffer !== "undefined") {
    const buf = Buffer.from(base64, "base64");
    return new Uint8Array(buf.buffer.slice(buf.byteOffset, buf.byteOffset + buf.byteLength));
  }
  const binary = atob(base64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export interface RpcDatePayload {
  __rpcType: "date";
  encoding: "iso8601";
  data: string;
}

export function isRpcDatePayload(val: unknown): val is RpcDatePayload {
  return (
    !!val &&
    typeof val === "object" &&
    (val as any).__rpcType === "date" &&
    (val as any).encoding === "iso8601" &&
    typeof (val as any).data === "string"
  );
}

export function decodeRpcBinary(input: unknown): unknown {
  if (input === null || input === undefined) return input;
  if (
    typeof input === "object" &&
    (input as any).__rpcType === "binary" &&
    (input as any).encoding === "base64" &&
    typeof (input as any).data === "string"
  ) {
    return base64ToUint8Array((input as any).data);
  }
  if (isRpcDatePayload(input)) {
    const parsed = new Date(input.data);
    if (Number.isNaN(parsed.getTime()) || !Number.isFinite(parsed.getTime())) {
      throw new AppError(ErrorCode.Validation, `Data corrompida recebida no envelope RPC: "${input.data}"`);
    }
    return parsed;
  }
  if (Array.isArray(input)) {
    return input.map(decodeRpcBinary);
  }
  if (typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      out[k] = decodeRpcBinary(v);
    }
    return out;
  }
  return input;
}

export function encodeRpcBinary(input: unknown): unknown {
  if (input === null || input === undefined) return input;

  if (input instanceof Date) {
    const time = input.getTime();
    if (!Number.isFinite(time) || Number.isNaN(time)) {
      throw new AppError(ErrorCode.Validation, "Data inválida no transporte RPC.");
    }
    return {
      __rpcType: "date",
      encoding: "iso8601",
      data: input.toISOString()
    };
  }

  if (input instanceof ArrayBuffer) {
    return {
      __rpcType: "binary",
      encoding: "base64",
      data: uint8ArrayToBase64(new Uint8Array(input))
    };
  }

  if (ArrayBuffer.isView(input)) {
    const view = input as ArrayBufferView;
    const sub = new Uint8Array(view.buffer, view.byteOffset, view.byteLength);
    return {
      __rpcType: "binary",
      encoding: "base64",
      data: uint8ArrayToBase64(sub)
    };
  }

  if (Array.isArray(input)) {
    return input.map(encodeRpcBinary);
  }

  if (typeof input === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input as Record<string, unknown>)) {
      out[k] = encodeRpcBinary(v);
    }
    return out;
  }

  return input;
}

export const platform = isRpc ? new Proxy(basePlatform, {
  get(target, serviceName) {
    const service = (target as any)[serviceName];
    if (serviceName === 'authorizationService' || typeof service !== 'object' || service === null) {
      return service;
    }
    return new Proxy(service, {
      get(svc, methodName) {
        if (typeof methodName === 'string' && typeof svc[methodName] === 'function') {
          const fullMethodName = `${String(serviceName)}.${methodName}`;
          if (LOCAL_SYNC_METHODS.has(fullMethodName) || methodName.startsWith('attach') || methodName.startsWith('register')) {
            // Executa localmente e sincronamente na instância do navegador
            return (...args: any[]) => svc[methodName](...args);
          }

          return async (...args: any[]) => {
            const encodedArgs = encodeRpcBinary(args);
            const res = await fetch('/api/rpc', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify({ service: serviceName, method: methodName, args: encodedArgs })
            });

            const contentType = res.headers.get('content-type') || '';
            if (!contentType.includes('application/json')) {
              const text = await res.text();
              throw new Error(`Falha de comunicação com o servidor (${res.status}): resposta não é JSON.`);
            }

            const data = await res.json();
            if (data.error) throw new Error(data.error);
            return decodeRpcBinary(data.result);
          };
        }
        return svc[methodName];
      }
    });
  }
}) : basePlatform;

declare global {
  interface Window {
    app: {
      platform: BrowserPlatform;
    }
  }
}

if (typeof window !== "undefined") {
  window.app = { platform: platform as BrowserPlatform };
}

